/**
 * AddBlockButton — tall `+ VIDEO BLOCK` / `+ AUDIO BLOCK` button pinned to the
 * right edge of a Video or Audio track. The label runs bottom-to-top so the
 * button fills the track's height in a narrow column. Clicking appends a new
 * empty block after the track's last block.
 */

import { memo } from 'react'
import { useTranslation } from 'react-i18next'

interface AddBlockButtonProps {
  track: 'video' | 'audio'
  onClick: () => void
  disabled?: boolean
}

export const AddBlockButton = memo(function AddBlockButton({
  track,
  onClick,
  disabled = false,
}: AddBlockButtonProps) {
  const { t } = useTranslation('canvas')
  const fullLabel = t('addBlockButton.addBlock', {
    type: track === 'video' ? t('addBlockButton.video') : t('addBlockButton.audio'),
  })
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      title={fullLabel}
      aria-label={fullLabel}
      className="flex-1 min-h-0 flex items-center justify-center gap-1.5 py-2 text-xxs uppercase tracking-wider text-text-muted hover:text-primary border border-dashed border-border-subtle hover:border-primary/40 rounded-lg transition-colors [writing-mode:vertical-rl] rotate-180 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary disabled:cursor-not-allowed disabled:border-input-border disabled:text-text-muted disabled:hover:border-input-border disabled:hover:text-text-muted"
      data-testid={`add-block-${track}`}
    >
      <svg className="w-3 h-3 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden>
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 4v16m8-8H4"
        />
      </svg>
      <span className="whitespace-nowrap">
        {track === 'video' ? t('addBlockButton.videoBlock') : t('addBlockButton.audioBlock')}
      </span>
    </button>
  )
})
